import { useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectAccessToken } from '@/store/slices/authSlice'
import { applyBalanceUpdate } from '@/store/slices/paymentSlice'
import { disconnectPlayerSocket, getPlayerSocket } from '@/store/socket/player-socket'
import type { BalanceUpdateSocketData, BalanceUpdateSocketResponse } from '@/types/payment'

export function parseBalanceUpdate(payload: unknown): BalanceUpdateSocketData | null {
	if (!payload || typeof payload !== 'object') return null

	const data = (payload as BalanceUpdateSocketResponse).data
	if (!data || typeof data.currency_code !== 'string' || !Number.isFinite(Number(data.balance))) {
		return null
	}

	return {
		currency_code: data.currency_code,
		balance: Number(data.balance),
	}
}

/**
 * Listens for Socket.IO `balanceUpdate` and syncs `payment.balances`.
 * Optional `onUpdate` runs after the store is updated (e.g. header animation).
 */
export function useBalanceUpdateSocket(onUpdate?: (data: BalanceUpdateSocketData) => void) {
	const dispatch = useAppDispatch()
	const token = useAppSelector(selectAccessToken)
	const onUpdateRef = useRef(onUpdate)
	onUpdateRef.current = onUpdate

	useEffect(() => {
		if (!token) {
			disconnectPlayerSocket()
			return
		}

		const socket = getPlayerSocket(token)
		const onBalanceUpdate = (payload: unknown) => {
			const data = parseBalanceUpdate(payload)
			if (!data) return

			dispatch(applyBalanceUpdate(data))
			onUpdateRef.current?.(data)
		}

		socket.on('balanceUpdate', onBalanceUpdate)
		return () => {
			socket.off('balanceUpdate', onBalanceUpdate)
		}
	}, [dispatch, token])
}
